/**
 * Copyright (c) HashiCorp, Inc.
 */

import Component from '@glimmer/component';

import { fn, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import {
  HdsButton,
  HdsDropdown,
} from '@hashicorp/design-system-components/components';

import FilterBarFilterCheckbox from './filter/checkbox.gts';
import FilterBarFilterCheckmark from './filter/checkmark.gts';
import FilterBarFilterRadio from './filter/radio.gts';
import FilterBarFilterGeneric from './filter/generic.gts';
import FilterBarFilterFilterInput from './filter/filter-input.gts';

import type { FilterConfig, ToggleArgs } from '../filter-bar';
import type { ComponentLike, WithBoundArgs } from '@glint/template';
import type { HdsDropdownToggleButton } from '@hashicorp/design-system-components/components';
import type { HdsDropdownSignature } from '@hashicorp/design-system-components/components/hds/dropdown/index';
import type { HdsDropdownListItemCopyItemSignature } from '@hashicorp/design-system-components/components/hds/dropdown/list-item/copy-item';
import type { HdsDropdownListItemDescriptionSignature } from '@hashicorp/design-system-components/components/hds/dropdown/list-item/description';
import type { HdsDropdownListItemInteractiveSignature } from '@hashicorp/design-system-components/components/hds/dropdown/list-item/interactive';
import type { HdsDropdownListItemSeparatorSignature } from '@hashicorp/design-system-components/components/hds/dropdown/list-item/separator';
import type { HdsDropdownListItemTitleSignature } from '@hashicorp/design-system-components/components/hds/dropdown/list-item/title';

export interface CutFilterBarFilterSignature {
  Args: {
    name: string;
    config: FilterConfig;
    localConfig: FilterConfig;
    toggle: (toggle: ToggleArgs) => void;
    softToggle: (toggle: ToggleArgs) => void;
    applyFilter: (name: string) => void;
    clearPendingFilter: (...args: unknown[]) => void;
    isChecked?: (
      localConfig: FilterConfig,
      name: string,
      value: unknown,
    ) => boolean;
    isMultiSelect?: boolean;
    batch?: boolean;
    dropdown?: ComponentLike<HdsDropdownSignature>;
    listPosition?: 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';
  };
  Blocks: {
    default: [
      {
        ToggleButton?: WithBoundArgs<
          typeof HdsDropdownToggleButton,
          'color' | 'count'
        >;
        Checkbox?: WithBoundArgs<
          typeof FilterBarFilterCheckbox,
          | 'checkbox'
          | 'filterName'
          | 'localConfig'
          | 'toggle'
          | 'isChecked'
          | 'isMultiSelect'
        >;
        Checkmark?: WithBoundArgs<
          typeof FilterBarFilterCheckmark,
          'checkmark' | 'filterName' | 'localConfig' | 'toggle' | 'isChecked'
        >;
        Radio?: WithBoundArgs<
          typeof FilterBarFilterRadio,
          'radio' | 'filterName' | 'localConfig' | 'toggle' | 'isChecked'
        >;
        Generic?: WithBoundArgs<
          typeof FilterBarFilterGeneric,
          'generic' | 'toggle' | 'config' | 'localConfig'
        >;
        FilterInput?: WithBoundArgs<
          typeof FilterBarFilterFilterInput,
          'components'
        >;
        CopyItem?: ComponentLike<HdsDropdownListItemCopyItemSignature>;
        Description?: ComponentLike<HdsDropdownListItemDescriptionSignature>;
        Interactive?: ComponentLike<HdsDropdownListItemInteractiveSignature>;
        Separator?: ComponentLike<HdsDropdownListItemSeparatorSignature>;
        Title?: ComponentLike<HdsDropdownListItemTitleSignature>;
      },
    ];
  };
  Element: HTMLDivElement;
}

/**
 * `Cut::FilterBar::Filter` is a wrapper of the HDS::Dropdown component that exposes wrapped dropdown list items
 * that handle hooking up the change handlers for updating filter bar state. It also provides some defaults to the UI
 * compoents, like filter counts to the toggle button.
 *
 * ```
 * <Cut::FilterBar as |FB|>
 *  <FB.Filter @name="status" as |F|>
 *    <F.ToggleButton @text="Status" />
 *    <F.Checkbox @name="Warning" @value="warning" />
 *  </FB.Filter>
 * </Cut::FilterBar>
 * ```
 *
 * @class Cut::FilterComponent
 *
 */
export default class CutFilterBarFilter extends Component<CutFilterBarFilterSignature> {
  get toggle(): (toggle: ToggleArgs) => void {
    return this.args.batch ? this.args.softToggle : this.args.toggle;
  }

  get filterCount(): string | undefined {
    if (!this.args.config?.filters || !this.args.isMultiSelect) {
      return undefined;
    }

    const value = this.args.config.filters[this.args.name];
    if (value && Array.isArray(value)) {
      return value.length.toString();
    } else if (value) {
      return '1';
    }
  }

  <template>
    {{! @glint-nocheck }}
    {{#if @dropdown}}
      {{#let @dropdown this.filterCount as |Dropdown|}}
        <Dropdown
          @listPosition={{if @listPosition @listPosition "bottom-right"}}
          @onClose={{fn @clearPendingFilter @name}}
          ...attributes
          as |DD|
        >
          {{yield
            (hash
              ToggleButton=(component
                DD.ToggleButton color="secondary" count=this.filterCount
              )
              Checkbox=(component
                FilterBarFilterCheckbox
                checkbox=DD.Checkbox
                filterName=@name
                localConfig=@localConfig
                toggle=this.toggle
                isChecked=@isChecked
                isMultiSelect=@isMultiSelect
              )
              Checkmark=(component
                FilterBarFilterCheckmark
                checkmark=DD.Checkmark
                filterName=@name
                localConfig=@localConfig
                toggle=this.toggle
                isChecked=@isChecked
                isMultiSelect=@isMultiSelect
              )
              Radio=(component
                FilterBarFilterRadio
                radio=DD.Radio
                filterName=@name
                localConfig=@localConfig
                toggle=this.toggle
                isChecked=@isChecked
              )
              Generic=(component
                FilterBarFilterGeneric
                generic=DD.Generic
                toggle=this.toggle
                applyFilter=@applyFilter
                config=@config
                localConfig=@localConfig
              )
              FilterInput=(component FilterBarFilterFilterInput components=DD)
              CopyItem=DD.CopyItem
              Description=DD.Description
              Interactive=DD.Interactive
              Separator=DD.Separator
              Title=DD.Title
            )
          }}
          {{#if @batch}}
            <DD.Footer
              @hasDivider={{true}}
              class="cut-filter-bar-apply-filter-button"
            >
              <HdsButton
                @text="Apply"
                @color="primary"
                @isFullWidth={{true}}
                {{on "click" (fn @applyFilter @name)}}
              />
            </DD.Footer>
          {{/if}}
        </Dropdown>
      {{/let}}
    {{else}}
      <HdsDropdown
        @listPosition={{if @listPosition @listPosition "bottom-right"}}
        @onClose={{fn @clearPendingFilter @name}}
        ...attributes
        as |DD|
      >
        {{yield
          (hash
            ToggleButton=(component
              DD.ToggleButton color="secondary" count=this.filterCount
            )
            Checkbox=(component
              FilterBarFilterCheckbox
              checkbox=DD.Checkbox
              filterName=@name
              localConfig=@localConfig
              toggle=this.toggle
              applyFilter=@applyFilter
              isChecked=@isChecked
              isMultiSelect=@isMultiSelect
            )
            Checkmark=(component
              FilterBarFilterCheckmark
              checkmark=DD.Checkmark
              filterName=@name
              localConfig=@localConfig
              toggle=this.toggle
              applyFilter=@applyFilter
              isChecked=@isChecked
              isMultiSelect=@isMultiSelect
            )
            Radio=(component
              FilterBarFilterRadio
              radio=DD.Radio
              filterName=@name
              localConfig=@localConfig
              toggle=this.toggle
              applyFilter=@applyFilter
              isChecked=@isChecked
              isMultiSelect=@isMultiSelect
            )
            Generic=(component
              FilterBarFilterGeneric
              generic=DD.Generic
              toggle=this.toggle
              applyFilter=@applyFilter
              config=@config
              localConfig=@localConfig
            )
            FilterInput=(component FilterBarFilterFilterInput components=DD)
            CopyItem=DD.CopyItem
            Description=DD.Description
            Interactive=DD.Interactive
            Separator=DD.Separator
            Title=DD.Title
          )
        }}

        {{#if @batch}}
          <DD.Footer
            @hasDivider={{true}}
            class="cut-filter-bar-apply-filter-button"
          >
            <HdsButton
              @text="Apply"
              @color="primary"
              @isFullWidth={{true}}
              {{on "click" (fn @applyFilter @name)}}
            />
          </DD.Footer>
        {{/if}}
      </HdsDropdown>
    {{/if}}
  </template>
}
